// ========================================
// Resentment List Component
// ========================================
// Work through and process difficult emotions

import { useState, FormEvent } from 'react';
import { useResentments } from '../../hooks/useResentments';
import { AFFECTED_AREAS } from '../../utils/emotionData';
import ResentmentItem from './ResentmentItem';
import './GratitudeResentment.css';

const ResentmentList = () => {
  const { resentments, loading, addResentment } = useResentments();

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    target: '',
    reason: '',
    affectedArea: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.target.trim()) {
      setError('Please enter who or what you resent');
      return;
    }

    if (!formData.reason.trim()) {
      setError('Please enter the reason for this resentment');
      return;
    }

    if (!formData.affectedArea) {
      setError('Please select an affected area');
      return;
    }

    setSubmitting(true);

    try {
      await addResentment({
        target: formData.target.trim(),
        reason: formData.reason.trim(),
        affectedArea: formData.affectedArea,
        notes: formData.notes.trim() || undefined,
      });

      // Reset form
      setFormData({ target: '', reason: '', affectedArea: '', notes: '' });
      setShowForm(false);
    } catch (err) {
      console.error('Error adding resentment:', err);
      setError('Failed to add resentment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ target: '', reason: '', affectedArea: '', notes: '' });
    setShowForm(false);
    setError('');
  };

  const activeResentments = resentments.filter((r) => r.status === 'active');
  const processingResentments = resentments.filter((r) => r.status === 'processing');
  const releasedResentments = resentments.filter((r) => r.status === 'released');

  if (loading) {
    return (
      <div className="list-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading resentments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="list-container">
      <div className="list-content">
        {/* Header */}
        <div className="list-header glass-panel">
          <div className="header-content">
            <h1>Process Resentments</h1>
            <p>Work through difficult emotions with structured reflection</p>
          </div>
          {!showForm && (
            <button onClick={() => setShowForm(true)} className="glass-button">
              Add Resentment
            </button>
          )}
        </div>

        {/* Add Form */}
        {showForm && (
          <div className="add-form glass-panel">
            <h2>Add New Resentment</h2>

            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="target">Who or what do you resent?</label>
                <input
                  type="text"
                  id="target"
                  value={formData.target}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                  placeholder="e.g., A situation, person, or institution"
                  className="glass-input"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="reason">Why? (What harm or threat did you perceive?)</label>
                <textarea
                  id="reason"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Describe the situation and what it meant to you..."
                  className="glass-input"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="affectedArea">What part of self was affected?</label>
                <select
                  id="affectedArea"
                  value={formData.affectedArea}
                  onChange={(e) => setFormData({ ...formData, affectedArea: e.target.value })}
                  className="glass-input"
                >
                  <option value="">Select an area...</option>
                  {AFFECTED_AREAS.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Additional Notes (Optional)</label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional context..."
                  className="glass-input"
                  rows={2}
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleCancel} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="glass-button" disabled={submitting}>
                  {submitting ? 'Adding...' : 'Add Resentment'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Resentments List */}
        {resentments.length === 0 ? (
          <div className="empty-state glass-panel">
            <h2>Process Difficult Emotions</h2>
            <p>
              Use this tool to work through resentments and conflicts. Naming and examining these feelings
              can help you find peace and move forward.
            </p>
          </div>
        ) : (
          <>
            {activeResentments.length > 0 && (
              <section className="list-section">
                <h2 className="section-title">Active</h2>
                <div className="items-grid">
                  {activeResentments.map((resentment) => (
                    <ResentmentItem key={resentment.id} resentment={resentment} />
                  ))}
                </div>
              </section>
            )}

            {processingResentments.length > 0 && (
              <section className="list-section">
                <h2 className="section-title">Processing</h2>
                <div className="items-grid">
                  {processingResentments.map((resentment) => (
                    <ResentmentItem key={resentment.id} resentment={resentment} />
                  ))}
                </div>
              </section>
            )}

            {releasedResentments.length > 0 && (
              <section className="list-section">
                <h2 className="section-title">Released</h2>
                <div className="items-grid">
                  {releasedResentments.map((resentment) => (
                    <ResentmentItem key={resentment.id} resentment={resentment} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ResentmentList;
